import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Euro, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';
import weeklyPricesFallback from '../data/weeklyPrices';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservation = () => {
  const [weeklyPrices, setWeeklyPrices] = useState(weeklyPricesFallback);
  const [availBlocks, setAvailBlocks] = useState({ available: null, booked: null });

  useEffect(() => {
    const loadWeekly = async () => {
      try {
        const res = await axios.get(`${API}/weekly-pricing`);
        if (Array.isArray(res.data) && res.data.length > 0) setWeeklyPrices(res.data);
      } catch (e) { console.warn('Tarifs hebdo non chargés, repli sur le fichier local:', e); }
    };
    const loadBlocks = async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/content/availability_available`),
          axios.get(`${API}/content/availability_booked`),
        ]);
        setAvailBlocks({ available: a.data, booked: b.data });
      } catch (e) { console.warn('Pavés de disponibilité non chargés:', e); }
    };
    loadWeekly();
    loadBlocks();
  }, []);

  const [selectedDates, setSelectedDates] = useState({
    from: undefined,
    to: undefined
  });

  // Semaines occupées : pilotées depuis le back-office (champ "booked" de chaque semaine)
  const parseD0 = (s) => new Date(s + 'T00:00:00');
  const generateBookedDates = () => {
    const dates = [];
    weeklyPrices.filter((w) => w.booked).forEach((w) => {
      const start = parseD0(w.start);
      const end = parseD0(w.end);
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
    });
    return dates;
  };
  const bookedDates = generateBookedDates();

  // Tarifs semaine par semaine (fichier Excel du propriétaire)
  const parseD = (s) => new Date(s + 'T00:00:00');
  const dOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const priceEntryForDate = (date) => {
    const d0 = dOnly(date);
    return weeklyPrices.find((w) => d0 >= parseD(w.start) && d0 < parseD(w.end)) || null;
  };

  // Repli saison pour les dates hors fichier (ex. printemps/été 2026)
  const seasonFallback = (date) => {
    const m = date.getMonth();
    if (m === 3 || m === 4) return 1500;
    if (m === 5) return 1800;
    if (m === 6 || m === 7) return 2400;
    if (m === 8 || m === 9) return 1600;
    if (m === 10) return 1400;
    return null;
  };

  const fmtEur = (n) => n.toLocaleString("fr-FR") + "€";
  const fmtDate = (d) => d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const fmtShort = (s) => parseD(s).toLocaleDateString("fr-FR");

  // Calcule le tarif de la sélection (par semaine)
  const computeSelection = () => {
    const from = selectedDates?.from;
    if (!from) return null;
    const DAY = 86400000;
    const to = selectedDates?.to || new Date(from.getTime() + 7 * DAY);
    const nights = Math.max(7, Math.round((to - from) / DAY));
    const weeks = Math.max(1, Math.round(nights / 7));
    let total = 0;
    let allPriced = true;
    let electra = false;
    const weekList = [];
    for (let i = 0; i < weeks; i++) {
      const start = new Date(from.getTime() + i * 7 * DAY);
      const end = new Date(start.getTime() + 6 * DAY);
      const entry = priceEntryForDate(start);
      const price = entry ? entry.price : seasonFallback(start);
      if (price == null) allPriced = false;
      else total += price;
      if (entry && entry.electra) electra = true;
      weekList.push({ start, end, price, electra: entry ? entry.electra : false });
    }
    return { from, to, weeks, total, allPriced, electra, weekList };
  };

  const selection = computeSelection();
  const isWeekSelected = (startStr) => {
    if (!selection) return false;
    const s = parseD(startStr);
    return s >= dOnly(selection.from) && s < dOnly(selection.to);
  };

  return (
    <div className="min-h-screen bg-stone-50 py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif mb-4 text-stone-800">Réservation & Tarifs</h1>
          <p className="text-lg text-stone-600">Tarifs 2026-2027 · Planning d'occupation à jour</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Calendar Section */}
          <Card className="shadow-lg border-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-3xl font-serif text-stone-800">
                <CalendarIcon className="w-8 h-8 text-amber-700" />
                Disponibilités
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Pavés de disponibilité (modifiables depuis le back-office) */}
              <div className="mb-6 space-y-3">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border-2 border-green-300">
                  <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xl font-bold text-green-800 leading-tight">
                      {availBlocks.available?.title || 'Encore disponible'}
                    </p>
                    <p className="text-lg text-green-900 mt-1 whitespace-pre-line">
                      {availBlocks.available?.content || '• Semaine du 21 au 27 juin 2026\n• Tout à partir du 6 septembre 2026\n• Le printemps (avril – mai)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border-2 border-red-300">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xl font-bold text-red-800 leading-tight">
                      {availBlocks.booked?.title || 'Déjà réservé (complet)'}
                    </p>
                    <p className="text-lg text-red-900 mt-1 whitespace-pre-line">
                      {availBlocks.booked?.content || '• Du 7 au 20 juin 2026\n• Du 28 juin au 5 septembre 2026'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <Calendar
                  mode="range"
                  selected={selectedDates}
                  onSelect={setSelectedDates}
                  disabled={bookedDates}
                  defaultMonth={new Date(2026, 8, 1)}
                  numberOfMonths={1}
                  modifiers={{ booked: bookedDates }}
                  modifiersClassNames={{
                    booked: 'bg-red-200 !text-red-800 line-through font-bold rounded-md'
                  }}
                  className="rounded-xl border-2 border-stone-200 shadow-sm"
                  classNames={{
                    caption_label: 'text-2xl font-bold text-stone-800',
                    nav_button: 'h-10 w-10 bg-white border-2 border-stone-300 rounded-md opacity-100 hover:bg-stone-100',
                    head_cell: 'w-14 text-base font-semibold text-stone-600',
                    cell: 'text-lg p-0',
                    day: 'h-14 w-14 p-0 text-xl font-semibold rounded-md hover:bg-amber-100',
                    day_selected: 'bg-amber-700 text-white hover:bg-amber-700 hover:text-white focus:bg-amber-700 focus:text-white',
                    day_disabled: 'opacity-100'
                  }}
                />
              </div>

              {/* Grande légende */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                  <div className="w-6 h-6 rounded bg-green-200 ring-2 ring-green-500"></div>
                  <span className="text-base font-medium text-stone-700">Libre</span>
                </div>
                <div className="flex items-center gap-3 p-2 rounded-lg bg-red-50">
                  <div className="w-6 h-6 rounded bg-red-200"></div>
                  <span className="text-base font-medium text-stone-700">Réservé</span>
                </div>
                <div className="flex items-center gap-3 p-2 rounded-lg bg-amber-50">
                  <div className="w-6 h-6 rounded bg-amber-700"></div>
                  <span className="text-base font-medium text-stone-700">Sélection</span>
                </div>
              </div>
              {/* Tarif de la semaine sélectionnée */}
              {selection && (
                <div className="mt-6 p-5 rounded-xl bg-amber-50 border-2 border-amber-300">
                  {selection.allPriced ? (
                    <>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-lg font-semibold text-stone-800">
                          {selection.weeks === 1 ? "Tarif de la semaine" : `Tarif (${selection.weeks} semaines)`}
                        </span>
                        <span className="text-3xl font-bold text-amber-700">{fmtEur(selection.total)}</span>
                      </div>
                      <div className="space-y-1">
                        {selection.weekList.map((w, i) => (
                          <div key={i} className="flex justify-between text-sm text-stone-700">
                            <span>Semaine du {fmtDate(w.start)} au {fmtDate(w.end)}</span>
                            <span className="font-medium">
                              {fmtEur(w.price)}
                              {w.electra && <span className="text-stone-400"> (+ élec.)</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                      {selection.electra && (
                        <p className="text-xs text-stone-500 mt-2">Électricité en sus, selon relevé du compteur (période hivernale)</p>
                      )}
                    </>
                  ) : (
                    <p className="text-base text-stone-700">
                      Tarif <strong>sur demande</strong> pour cette période (hors saison). Contactez-nous pour un devis personnalisé.
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6">
                <p className="text-base text-stone-700 mb-4">
                  Sélectionnez vos dates sur le calendrier : le tarif de la semaine s'affiche automatiquement.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 text-lg transition-all duration-300 hover:scale-105">
                    Demander une Réservation
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Section */}
          <Card className="shadow-lg border-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-serif text-stone-800">
                <Euro className="w-6 h-6 text-amber-700" />
                Tarifs par Semaine
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-stone-500 mb-3">Du 5 septembre 2026 au 11 septembre 2027 — tarif par semaine (samedi à samedi)</p>
              <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                {weeklyPrices.map((w, index) => {
                  const sel = isWeekSelected(w.start);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border transition-all duration-200 ${
                        sel
                          ? 'border-amber-500 ring-2 ring-amber-400 bg-amber-50 shadow-md'
                          : 'border-stone-100 bg-gradient-to-r from-stone-50 to-amber-50/40 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium text-stone-800">{fmtShort(w.start)}</span>
                          <span className="text-stone-400 text-sm"> → {fmtShort(w.end)}</span>
                        </div>
                        <span className="text-xl font-bold text-amber-700 whitespace-nowrap">{fmtEur(w.price)}</span>
                      </div>
                      {w.electra && (
                        <span className="text-xs text-stone-500">+ électricité (selon relevé du compteur)</span>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-lg border-2 border-amber-200">
                <h4 className="font-semibold text-stone-800 mb-3">Informations Importantes</h4>
                <ul className="space-y-2 text-sm text-stone-700">
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Location à la semaine uniquement (samedi à samedi)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Caution : 500€ (restitué après état des lieux)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Ménage de fin de séjour : 180€</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Draps et linge de maison non fournis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Période hivernale : électricité en sus, selon relevé du compteur</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-700 mr-2">•</span>
                    <span>Spa de nage inclus (Avril à Novembre)</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info */}
        <Card className="shadow-lg border-none">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif mb-4 text-stone-800">Modalités de Réservation</h3>
            <div className="space-y-3 text-stone-700">
              <p>
                Pour réserver la Villa Nomade, veuillez nous contacter via le formulaire de contact ou par téléphone. 
                Un acompte de 30% sera demandé à la réservation, le solde étant dû 30 jours avant l'arrivée.
              </p>
              <p>
                <strong>Arrivée :</strong> Samedi entre 16h00 et 19h00<br />
                <strong>Départ :</strong> Samedi avant 10h00
              </p>
              <p className="text-amber-700 font-medium">
                Référencement d'hébergement N°17286 000168 PF
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reservation;